/**
 * FILE: packages/utils/src/date.ts
 * PURPOSE: Shared business utility.
 * AGENT_CONTEXT: Pure functions. No React, no Supabase client imports.
 * LAST_REVIEWED: 2026-05-07
 */

import { attemptMonthToNumber, canonicalAttemptForStorage } from './attemptCanonical.ts'

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

export function formatDisplayDate(iso: string | null | undefined): string {
  if (!iso) return '—'
  // Postgres timestamptz / full ISO strings (e.g. 2026-04-14T12:33:20+00:00)
  if (iso.includes('T') || iso.length > 10) {
    const d = new Date(iso)
    if (Number.isNaN(d.getTime())) return '—'
    return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
  }
  const [y = '', m = '', d = ''] = iso.split('-')
  const monthLabel = MONTH_NAMES[Number.parseInt(m, 10) - 1] ?? m
  return `${d} ${monthLabel} ${y}`.trim()
}

export function today(): string {
  return new Date().toISOString().split('T')[0] ?? ''
}

export function addMonthsToDate(dateStr: string, months: number): string | null {
  const d = new Date(dateStr)
  if (isNaN(d.getTime())) return null
  d.setMonth(d.getMonth() + months)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

export function attemptToValidTill(attempt: string): string | null {
  const stored = canonicalAttemptForStorage(attempt)
  const [monthCode, yearCode] = stored.split('_')
  if (!monthCode || !yearCode) return null
  const month = attemptMonthToNumber(monthCode)
  const year = yearCode.length === 2 ? `20${yearCode}` : yearCode
  if (!month) return null
  const lastDay = new Date(Number.parseInt(year, 10), Number.parseInt(month, 10), 0).getDate()
  return `${year}-${month}-${String(lastDay).padStart(2, '0')}`
}

export function calcValidTillFromCatalogDuration(
  enrollmentDate: string,
  value: number | null | undefined,
  unit: string | null | undefined
): string | null {
  if (!value || !unit) return null
  const normalizedUnit = unit.trim().toLowerCase()
  if (!enrollmentDate) return null

  if (normalizedUnit === 'months' || normalizedUnit === 'month') {
    return addMonthsToDate(enrollmentDate, value)
  }
  if (normalizedUnit === 'days' || normalizedUnit === 'day') {
    const d = new Date(enrollmentDate)
    if (isNaN(d.getTime())) return null
    d.setDate(d.getDate() + value)
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
  }
  if (normalizedUnit === 'years' || normalizedUnit === 'year') {
    return addMonthsToDate(enrollmentDate, value * 12)
  }
  return null
}

export function calcValidTill(
  course: string,
  attempt?: string,
  comboLevels?: string,
  enrollmentDate?: string,
  revisionMonths?: number,
  accessDurationValue?: number | null,
  accessDurationUnit?: string | null
): string | null {
  const base = enrollmentDate || today()
  if (comboLevels) return addMonthsToDate(base, comboLevels === 'L1_L2_L3' ? 36 : 24)
  if (course?.startsWith('REV_') || course === 'REV_PACK') {
    if (revisionMonths) return addMonthsToDate(base, revisionMonths)
  }
  if (course === 'CDP' || course === 'CHALLENGERS') return addMonthsToDate(base, revisionMonths ?? 3)
  if (course === 'Financial_Modeling' || course === 'FM') return addMonthsToDate(base, 6)
  if (attempt) return attemptToValidTill(attempt)
  return calcValidTillFromCatalogDuration(base, accessDurationValue, accessDurationUnit)
}

export function validTillStatus(iso: string | null | undefined): 'ok' | 'expiring' | 'expired' | 'empty' {
  if (!iso) return 'empty'
  const diff = (new Date(iso).getTime() - Date.now()) / 86400000
  if (diff < 0) return 'expired'
  if (diff < 30) return 'expiring'
  return 'ok'
}

export function formatRelativeDate(iso: string): string {
  const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000)
  if (diff === 0) return 'Today'
  if (diff === 1) return 'Yesterday'
  if (diff < 7) return `${diff} days ago`
  if (diff < 30) return `${Math.floor(diff / 7)} weeks ago`
  if (diff < 365) return `${Math.floor(diff / 30)} months ago`
  return `${Math.floor(diff / 365)} years ago`
}

/** Days between today and a date string (YYYY-MM-DD). Negative means already past. */
export function daysRemaining(validTill: string | null): number | null {
  if (!validTill) return null
  const end = new Date(`${validTill}T00:00:00`)
  const start = new Date(`${today()}T00:00:00`)
  return Math.ceil((end.getTime() - start.getTime()) / (24 * 60 * 60 * 1000))
}
